import {
  ClockCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  FolderFilled,
  FolderOutlined,
  MoreOutlined,
  PlusCircleOutlined,
  StarOutlined,
  TeamOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Flex, Skeleton, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  brand,
  neutral,
  semantic
} from "../../../../components/widgets/tokens.ts"
import type { ReportFolderNavItem } from "../../helpers/reportFolders.ts"
import {
  type ReportsNav,
  type ReportsQuickView,
  sameNav
} from "../../helpers/reportsNav.ts"

const { Text } = Typography

const QUICK_VIEWS: {
  view: ReportsQuickView
  labelKey: string
  icon: React.ReactNode
}[] = [
  {
    view: "all",
    labelKey: "reports.rail.nav.all",
    icon: <FileTextOutlined />
  },
  {
    view: "favorites",
    labelKey: "reports.rail.nav.favorites",
    icon: <StarOutlined />
  },
  {
    view: "recent",
    labelKey: "reports.rail.nav.recent",
    icon: <ClockCircleOutlined />
  },
  {
    view: "shared",
    labelKey: "reports.rail.nav.shared",
    icon: <TeamOutlined />
  }
]

const SELECTED_BG = "rgba(42,125,140,.10)"

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase"
}

interface NavRowProps {
  selected: boolean
  icon: React.ReactNode
  label: string
  count?: number
  menu?: React.ReactNode
  onClick: () => void
  testId?: string
}

const NavRow = ({
  selected,
  icon,
  label,
  count,
  menu,
  onClick,
  testId
}: NavRowProps) => (
  <div
    data-testid={testId}
    className="group flex items-center gap-1 rounded-lg"
    style={{ background: selected ? SELECTED_BG : "transparent" }}
  >
    <button
      type="button"
      onClick={onClick}
      aria-current={selected ? "page" : undefined}
      className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-lg border-0 bg-transparent py-2 pl-3 pr-1 text-left"
      style={{
        fontWeight: selected ? 600 : 500,
        color: selected ? brand.teal : neutral.ink
      }}
    >
      <span aria-hidden className="flex shrink-0 text-[15px]">
        {icon}
      </span>
      <Text
        ellipsis={{ tooltip: label }}
        className="min-w-0 flex-1"
        style={{ color: "inherit", fontWeight: "inherit" }}
      >
        {label}
      </Text>
      {count !== undefined && (
        <span
          className="shrink-0 text-xs tabular-nums"
          style={{ color: neutral.muted }}
        >
          {count}
        </span>
      )}
    </button>
    <span className="flex w-7 shrink-0 justify-center">{menu}</span>
  </div>
)

interface ReportsRailProps {
  activeNav: ReportsNav | null
  folders: ReportFolderNavItem[]
  unfiledCount: number
  loading: boolean
  onNavigate: (nav: ReportsNav) => void
  onCreateFolder: () => void
  onRenameFolder: (name: string) => void
  onDeleteFolder: (name: string) => void
}

/**
 * The Reports sidebar: quick views, then the folders. Folders are managed
 * only here — the table never lists them — and a folder row lists no report
 * names, only how many of the caller's reports it holds.
 */
export const ReportsRail = ({
  activeNav,
  folders,
  unfiledCount,
  loading,
  onNavigate,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder
}: ReportsRailProps) => {
  const { t } = useTranslation()

  const folderMenu = (folder: ReportFolderNavItem) =>
    folder.manageable ? (
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        menu={{
          items: [
            {
              key: "rename",
              icon: <EditOutlined />,
              label: t("reports.folders.renameAction"),
              onClick: () => onRenameFolder(folder.name)
            },
            {
              key: "delete",
              icon: <DeleteOutlined />,
              danger: true,
              label: t("reports.folders.deleteAction"),
              onClick: () => onDeleteFolder(folder.name)
            }
          ]
        }}
      >
        <Button
          type="text"
          size="small"
          icon={<MoreOutlined aria-hidden />}
          aria-label={t("reports.folders.menuFor", { name: folder.name })}
        />
      </Dropdown>
    ) : null

  return (
    <nav
      aria-label={t("reports.rail.label")}
      className="flex min-h-0 w-[240px] flex-none flex-col gap-4 overflow-y-auto border-0 border-r border-solid pr-4"
      style={{ borderColor: neutral.border }}
    >
      <Flex vertical gap={2}>
        <Text type="secondary" style={sectionLabelStyle} className="px-3 pb-1">
          {t("reports.rail.quickViews")}
        </Text>
        {QUICK_VIEWS.map((item) => {
          const nav: ReportsNav = { kind: "quick", view: item.view }
          return (
            <NavRow
              key={item.view}
              selected={sameNav(activeNav, nav)}
              icon={item.icon}
              label={t(item.labelKey)}
              onClick={() => onNavigate(nav)}
            />
          )
        })}
      </Flex>

      <div
        className="border-0 border-t border-solid"
        style={{ borderColor: neutral.border }}
      />

      <Flex vertical gap={2} data-testid="rail-folders">
        <Flex align="center" justify="space-between" className="pb-1 pl-3">
          <Text type="secondary" style={sectionLabelStyle}>
            {t("reports.rail.folders")}
          </Text>
          <Button
            type="link"
            size="small"
            icon={<PlusCircleOutlined aria-hidden />}
            onClick={onCreateFolder}
          >
            {t("reports.folders.newFolder")}
          </Button>
        </Flex>

        {loading && folders.length === 0 ? (
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        ) : (
          <>
            {folders.map((folder) => (
              <NavRow
                key={folder.name}
                testId="rail-folder"
                selected={sameNav(activeNav, {
                  kind: "folder",
                  name: folder.name
                })}
                icon={<FolderFilled style={{ color: semantic.star }} />}
                label={folder.name}
                count={folder.count}
                menu={folderMenu(folder)}
                onClick={() =>
                  onNavigate({ kind: "folder", name: folder.name })
                }
              />
            ))}
            {folders.length === 0 && (
              <Text type="secondary" className="px-3 text-[13px]">
                {t("reports.rail.noFolders")}
              </Text>
            )}
            <NavRow
              testId="rail-unfiled"
              selected={sameNav(activeNav, { kind: "unfiled" })}
              icon={<FolderOutlined style={{ color: neutral.muted }} />}
              label={t("reports.folders.unfiled")}
              count={unfiledCount}
              onClick={() => onNavigate({ kind: "unfiled" })}
            />
          </>
        )}
      </Flex>
    </nav>
  )
}
